import { HomePracticeStart } from "../HomePracticeStart.js";
import { PracticeTaskMaterials } from "../PracticeTaskMaterials.js";
import { NeckDiagramSheets } from "../neckDiagrams/NeckDiagramView.js";
import { LessonNoteText } from "../LessonNoteText.js";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useInRouterContext, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { get, type LessonNoteFile, type MyStudentDetail, type PracticeSession } from "../api.js";
import { useAuth, useOptionalAuth } from "../auth.js";
import { FastStudentDashboard } from "../booking/FastStudentDashboard.js";
import { InboxSummary } from "../inbox/summary.js";
import {
  historyDaysOf,
  sessionsOf,
  tutorsOf,
  useHomePracticeHistory,
  useHomePracticeLog,
  useHomeTutors,
  weeklyPracticeComparison,
  type WeeklyPracticeComparison,
} from "../familyHomeReads.js";
import { formatDate, formatDuration, formatTime } from "../format.js";
import { grooveSnapshotHref } from "../grooves.js";
import { LessonNoteGrooves } from "../LessonNoteGrooves.js";
import { NoteRecipients } from "../NoteRecipients.js";
import { Pagination } from "../Pagination.js";
import { getFamilyPracticeTodos, type PracticeTodo } from "../practiceTodos.js";
import { getFamilyStudentGoals } from "../studentGoals.js";
import {
  getFamilyRepertoire,
  REPERTOIRE_STATUS_LABELS,
  type RepertoireEntry,
} from "../repertoire.js";
import { homeTutorSubjects, homeTutorSummary } from "../homeTutorSummary.js";
import { TutorChips } from "../TutorProfiles.js";
import type { TutorPresentation } from "../tutorProfileApi.js";
import { Dialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import "../familyLessonNotes.css";
import "../familyHome.css";
import { NoteVisibility, type FamilyNoteIdentity } from "../engagement/NoteVisibility.js";

/** How many lesson notes Home asks the Hub to return. */
export const HOME_NOTE_PREVIEW = 3;

const FAMILY_LESSON_NOTE_PAGE_SIZE = CARD_RECORD_PAGE_SIZE;
// The learner scope lives in this browser history entry while `page` stays
// addressable for refreshes and back/forward navigation.
const FAMILY_NOTES_SCOPE_KEY = "familyNotesBrowse";
const FAMILY_NOTES_COLLECTION = "lesson-notes";

interface FamilyNotesScope {
  accountId: string;
  studentId: string;
  collection: typeof FAMILY_NOTES_COLLECTION;
}

function familyNotesPage(value: string | null): number {
  if (value === null) return 1;
  const page = Number(value);
  return Number.isSafeInteger(page) && page >= 1 ? page : 1;
}

function familyNotesPageSearch(search: string, page: number, studentId?: string): string {
  const params = new URLSearchParams();
  const selectedStudent = studentId ?? new URLSearchParams(search).get("student");
  if (selectedStudent) params.set("student", selectedStudent);
  const selectedLesson = new URLSearchParams(search).get("lesson");
  if (selectedLesson) params.set("lesson", selectedLesson);
  if (page > 1) params.set("page", String(page));
  const query = params.toString();
  return query ? `?${query}` : "";
}

function familyNotesScopeFromState(state: unknown): unknown | null {
  if (typeof state !== "object" || state === null || Array.isArray(state)) return null;
  if (!Object.prototype.hasOwnProperty.call(state, FAMILY_NOTES_SCOPE_KEY)) return null;
  return (state as Record<string, unknown>)[FAMILY_NOTES_SCOPE_KEY] ?? {};
}

function isFamilyNotesScope(value: unknown, scope: FamilyNotesScope): boolean {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const candidate = value as Partial<FamilyNotesScope>;
  return candidate.accountId === scope.accountId
    && candidate.studentId === scope.studentId
    && candidate.collection === scope.collection;
}

function familyNotesRouteState(state: unknown, scope: FamilyNotesScope): Record<string, unknown> {
  const current = typeof state === "object" && state !== null && !Array.isArray(state)
    ? state as Record<string, unknown>
    : {};
  return { ...current, [FAMILY_NOTES_SCOPE_KEY]: scope };
}

export interface FamilyHomeProps {
  studentId: string | null;
  booking: boolean;
}

/** Keep family Home actions inside the SPA while preserving isolated SSR renders. */
function FamilyHomeLink({
  to,
  className,
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link className={className} to={to}>
      {children}
    </Link>
  ) : (
    <a className={className} href={to}>
      {children}
    </a>
  );
}

/**
 * The selected student's home screen.
 *
 * Once a student id is known, booking Home, the overview record, tutor
 * presentation and the practice reads are independent. A slow WordPress
 * profile lookup therefore cannot hold up lesson times or the rest of Home.
 * The Inbox summary costs no request of its own — it reads the count the
 * shell already fetched for the sidebar badge.
 *
 * The selected learner, next lesson and current task lead. Other lesson and
 * account detail stays in disclosures or the dedicated workspace links.
 */
export function FamilyHome({ studentId, booking }: FamilyHomeProps) {
  const { data, error, loading, reload } = useAsync<MyStudentDetail | null>(
    () => (studentId
      ? get<MyStudentDetail>(`/me/students/${encodeURIComponent(studentId)}`, {
          lessonNotesPage: "1",
          lessonNotesPageSize: String(HOME_NOTE_PREVIEW),
        })
      : Promise.resolve(null)),
    [studentId],
  );
  const overview = data?.student.id === studentId ? data : null;
  const overviewLoading = loading || (data !== null && overview === null);
  const tutors = useHomeTutors(studentId);
  const practiceLog = useHomePracticeLog(studentId);
  const practiceHistory = useHomePracticeHistory(studentId);
  const accountId = useOptionalAuth()?.me?.user.id ?? null;
  const practiceTasks = useAsync(
    async () => ({ accountId, studentId, todos: studentId ? await getFamilyPracticeTodos(studentId) : [] }),
    [accountId, studentId],
  );
  if (!studentId) return null;

  const currentPracticeTasks = familyHomePracticeTasksForScope(practiceTasks.data, accountId, studentId);
  const lessonNotesError = overview ? error : data === null ? error : null;
  const practiceSessions = sessionsOf(practiceLog.data);
  const showLessonNotes = Boolean(overview?.lessons.length)
    || (overviewLoading && !overview)
    || Boolean(lessonNotesError);
  const showPracticeNotes = practiceSessions.length > 0
    || Boolean(practiceLog.error)
    || (practiceLog.loading && !practiceLog.data)
    || Boolean(practiceLog.data);

  const summary = <InboxSummary />;
  const supporting = (
    <details className="ui-disclosure student-home-supporting-details">
      <summary>Tutors and weekly practice</summary>
      <div className="ui-disclosure-body">
        <FamilyHomeTop
          studentId={studentId}
          tutors={tutorsOf(tutors.data)}
          tutorsLoading={tutors.loading && !tutors.data}
          practice={
            practiceHistory.data
              ? weeklyPracticeComparison(historyDaysOf(practiceHistory.data))
              : null
          }
          practiceLoading={practiceHistory.loading && !practiceHistory.data}
        />
      </div>
    </details>
  );

  return (
    <>
      {overview ? (
        <StudentHeading data={overview} tutors={tutorsOf(tutors.data)} />
      ) : overviewLoading ? (
        <p className="small muted" aria-live="polite">
          Loading student overview…
        </p>
      ) : null}

      {error && (
        <div className="notice small">
          Some overview details could not be loaded. Your lesson and update sections can still be
          used.
        </div>
      )}

      {booking ? (
        <FastStudentDashboard
          studentId={studentId}
          learning={{ data: overview, error, loading: overviewLoading, reload }}
          afterNextLesson={
            <FamilyHomePracticeTask
              key={studentId}
              studentId={studentId}
              todos={currentPracticeTasks}
              error={practiceTasks.error}
              loading={practiceTasks.loading && !practiceTasks.data}
              onRetry={practiceTasks.reload}
            />
          }
          afterPrimaryAction={
            <>
              {summary}
              <FamilyHomeMilestone studentId={studentId} />
            </>
          }
          afterCredits={supporting}
        />
      ) : overview ? (
        <div className="student-home-columns">
          <div className="student-home-column-main">
            <NextLessons upcomingBookings={overview.upcomingBookings} />
            <FamilyHomePracticeTask
              key={studentId}
              studentId={studentId}
              todos={currentPracticeTasks}
              error={practiceTasks.error}
              loading={practiceTasks.loading && !practiceTasks.data}
              onRetry={practiceTasks.reload}
            />
            {summary}
            <FamilyHomeMilestone studentId={studentId} />
          </div>
          <aside className="student-home-column-side">{supporting}</aside>
        </div>
      ) : overviewLoading ? (
        <p className="empty">Loading your lessons…</p>
      ) : null}

      {(showLessonNotes || showPracticeNotes) && (
        <div className="student-home-columns student-home-notes-columns">
          {showLessonNotes && (
            <LessonNoteCards
              studentId={studentId}
              lessons={overview?.lessons ?? []}
              loading={overviewLoading && !overview}
              error={lessonNotesError}
              onRetry={reload}
            />
          )}
          {showPracticeNotes && (
            <PracticeNoteCards
              sessions={practiceSessions}
              loading={practiceLog.loading && !practiceLog.data}
              error={practiceLog.error}
            />
          )}
        </div>
      )}
    </>
  );
}

/** The compact strip at the top of Home: who teaches, a way into Repertoire, this week's practice. */
export function FamilyHomeTop({
  studentId,
  tutors,
  tutorsLoading = false,
  practice,
  practiceLoading = false,
}: {
  studentId: string;
  tutors: readonly TutorPresentation[];
  tutorsLoading?: boolean;
  practice: WeeklyPracticeComparison | null;
  practiceLoading?: boolean;
}) {
  const heading = tutors.length === 1 ? "Your tutor" : "Your tutors";
  return (
    <section className="student-home-top" aria-label="Your tutors, repertoire and practice">
      <div className="panel student-home-tutor-strip">
        <div className="student-home-strip-head">
          <h2 className="student-home-strip-heading">{heading}</h2>
          <RepertoireButton studentId={studentId} />
        </div>
        {tutors.length > 0 ? (
          <TutorChips tutors={tutors.map((tutor) => ({ ...tutor, instruments: homeTutorSubjects(tutor) }))} />
        ) : tutorsLoading ? (
          <p className="small muted student-home-strip-empty" aria-live="polite">
            Loading tutor details…
          </p>
        ) : (
          <p className="small muted student-home-strip-empty">No tutor is assigned yet.</p>
        )}
      </div>
      <PracticeStats practice={practice} loading={practiceLoading} />
    </section>
  );
}

/** Never render a previous account or learner's task response while a new read is pending. */
export function familyHomePracticeTasksForScope(
  data: { accountId: string | null; studentId: string | null; todos: PracticeTodo[] } | null,
  accountId: string | null,
  studentId: string,
): PracticeTodo[] {
  return data?.accountId === accountId && data.studentId === studentId && Array.isArray(data.todos) ? data.todos : [];
}

/** The first active task remains the default for callers that need a single suggestion. */
export function nextFamilyHomePracticeTask(
  todos: readonly PracticeTodo[],
): PracticeTodo | null {
  return todos.find((todo) => !todo.completed && !todo.withdrawnAt) ?? null;
}

export function FamilyHomePracticeTask({
  studentId,
  todo = null,
  todos,
  error = null,
  loading = false,
  onRetry,
}: {
  studentId?: string;
  todo?: PracticeTodo | null;
  todos?: readonly PracticeTodo[];
  error?: string | null;
  loading?: boolean;
  onRetry?: () => void;
}) {
  if (loading) {
    return <p className="small muted" role="status">Loading current practice tasks…</p>;
  }
  if (error) {
    return (
      <section className="student-home-next-task" aria-labelledby="student-home-next-task-heading">
        <div className="notice small" role="alert">
          <h2 id="student-home-next-task-heading" className="student-home-strip-heading">Current practice tasks</h2>
          <p>Could not load current practice tasks. Retry or open Practice to try again.</p>
          <div className="student-home-next-task-action">
            {onRetry && <button type="button" className="quiet small" onClick={onRetry}>Retry loading tasks</button>}
            <FamilyHomeLink className="small student-home-strip-link" to="/practice">Open Practice</FamilyHomeLink>
          </div>
        </div>
      </section>
    );
  }
  const active = (todos ?? (todo ? [todo] : [])).filter((task) => !task.completed && !task.withdrawnAt);
  if (!active.length) return null;

  const renderTask = (task: PracticeTodo) => (
    <article key={task.id} aria-label={"Practice task: " + task.title}>
      <strong>{task.title}</strong>
      {task.readyForReviewAt ? <span className="tag">Ready for tutor review</span> : <span className="small muted"> · Keep practising</span>}
      {task.repertoireTitle && (
        <p className="small muted">Repertoire: {task.repertoireComposer ? task.repertoireComposer + " — " : ""}{task.repertoireTitle}</p>
      )}
      {task.description && <p className="small muted">{task.description}</p>}
      {(task.suggestedMinutes != null || task.practiceSeconds > 0) && (
        <p className="small muted">
          {task.suggestedMinutes != null && <>Aim for {task.suggestedMinutes} min/day</>}
          {task.suggestedMinutes != null && task.practiceSeconds > 0 && " · "}
          {task.practiceSeconds > 0 && <>Practised {formatDuration(task.practiceSeconds)}</>}
        </p>
      )}
      <PracticeTaskMaterials materials={task.materials} practiceTarget={task.id} />
      <div className="student-home-next-task-action">
        {studentId && task.id === active[0]?.id && <HomePracticeStart key={studentId + ":" + task.id} studentId={studentId} taskId={task.id} />}
        <FamilyHomeLink className="button quiet" to={"/practice?log_title=" + encodeURIComponent(task.title.trim() || "Practice session")}>Log practice</FamilyHomeLink>
        <FamilyHomeLink className="button action-secondary" to="/practice">Open Practice</FamilyHomeLink>
        {studentId && task.sourceLessonId && <FamilyHomeLink className="button quiet"
          to={"/notes?student=" + encodeURIComponent(studentId) + "&lesson=" + encodeURIComponent(task.sourceLessonId)}>View lesson note</FamilyHomeLink>}
      </div>
    </article>
  );

  return (
    <section className="student-home-next-task" aria-labelledby="student-home-next-task-heading">
      <div className="panel">
        <div className="student-home-strip-head">
          <h2 id="student-home-next-task-heading" className="student-home-strip-heading">
            {active.length === 1 ? "Current practice task" : "Current practice tasks"}
          </h2>
          <span className="small muted">{active.length} {active.length === 1 ? "task" : "tasks"}</span>
        </div>
        <div className="stack">
          {renderTask(active[0]!)}
          {active.length > 1 && (
            <details className="ui-disclosure student-home-more-practice">
              <summary>Other practice tasks ({active.length - 1})</summary>
              <div className="ui-disclosure-body stack">
                {active.slice(1).map(renderTask)}
              </div>
            </details>
          )}
        </div>
      </div>
    </section>
  );
}

/** Show the most recently updated family-visible goal as the learner's current milestone. */
export function FamilyHomeMilestone({ studentId }: { studentId: string }) {
  const result = useAsync(
    async () => ({
      studentId,
      goals: await getFamilyStudentGoals(studentId),
    }),
    [studentId],
  );
  const scoped = result.data?.studentId === studentId ? result.data.goals : null;

  if (result.loading && !scoped) {
    return (
      <p className="small muted" role="status" aria-live="polite">
        Loading your learning milestone…
      </p>
    );
  }
  if (result.error) {
    return (
      <section
        className="notice small"
        role="alert"
        aria-labelledby="student-home-milestone-error-heading"
      >
        <h2 id="student-home-milestone-error-heading" className="student-home-strip-heading">
          Learning milestone
        </h2>
        <p>Your current milestone could not be loaded.</p>
        <div className="student-home-next-task-action">
          <button type="button" className="quiet small" onClick={result.reload}>
            Retry loading milestone
          </button>
          <FamilyHomeLink className="small student-home-strip-link" to="/progress">
            View progress
          </FamilyHomeLink>
        </div>
      </section>
    );
  }

  const goal = scoped?.active[0];
  if (!goal) return null;

  return (
    <section className="panel student-home-milestone" aria-labelledby="student-home-milestone-heading">
      <div className="student-home-strip-head">
        <h2 id="student-home-milestone-heading" className="student-home-strip-heading">
          Current learning milestone
        </h2>
        <FamilyHomeLink className="small student-home-strip-link" to="/progress">
          View progress
        </FamilyHomeLink>
      </div>
      <strong>{goal.title}</strong>
      {goal.repertoireTitle && (
        <p className="small muted">
          Repertoire: {goal.repertoireComposer ? `${goal.repertoireComposer} — ` : ""}{goal.repertoireTitle}
        </p>
      )}
      {goal.targetDate && (
        <p className="small muted">Target date: {formatDate(goal.targetDate + "T12:00:00Z")}</p>
      )}
      {goal.description && <p className="small muted">{goal.description}</p>}
    </section>
  );
}

/** Words for the difference between this week and last, in the same units as the total. */
export function describeWeeklyChange(practice: WeeklyPracticeComparison): string {
  const { deltaSeconds, lastWeekSeconds, thisWeekSeconds } = practice;
  if (thisWeekSeconds === 0 && lastWeekSeconds === 0) return "Nothing logged last week either.";
  if (lastWeekSeconds === 0) return "Nothing was logged last week.";
  if (deltaSeconds === 0) return "The same as last week.";
  if (deltaSeconds > 0) return `Up ${formatDuration(deltaSeconds)} on last week.`;
  return `Down ${formatDuration(-deltaSeconds)} on last week.`;
}

export function PracticeStats({
  practice,
  loading = false,
}: {
  practice: WeeklyPracticeComparison | null;
  loading?: boolean;
}) {
  return (
    <div className="panel student-home-practice-stats" aria-labelledby="practice-stats-heading">
      <div className="student-home-strip-head">
        <h2 id="practice-stats-heading" className="student-home-strip-heading">
          Practice this week
        </h2>
        <FamilyHomeLink className="small student-home-strip-link" to="/practice">
          Practice
        </FamilyHomeLink>
      </div>
      {practice ? (
        <>
          <div className="student-home-practice-total tabular">
            {practice.thisWeekSeconds > 0 ? formatDuration(practice.thisWeekSeconds) : "0m"}
          </div>
          <div className="small muted">
            {practice.thisWeekDays === 0
              ? "No practice logged yet."
              : `${practice.thisWeekDays} ${practice.thisWeekDays === 1 ? "day" : "days"} practised.`}
          </div>
          <div
            className={`small student-home-practice-change${
              practice.deltaSeconds > 0 ? " is-up" : practice.deltaSeconds < 0 ? " is-down" : ""
            }`}
          >
            {describeWeeklyChange(practice)}
          </div>
        </>
      ) : loading ? (
        <p className="small muted student-home-strip-empty" aria-live="polite">
          Loading practice…
        </p>
      ) : (
        <p className="small muted student-home-strip-empty">Practice totals are not available.</p>
      )}
    </div>
  );
}

function pieceName(entry: RepertoireEntry): string {
  return entry.composer ? `${entry.composer} — ${entry.title}` : entry.title;
}

/** Repertoire opens in place; the full Progress workspace stays one link away. */
export function RepertoireButton({ studentId }: { studentId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="action-secondary small"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        Repertoire
      </button>
      {open && <RepertoireDialog studentId={studentId} onClose={() => setOpen(false)} />}
    </>
  );
}

export function RepertoireDialog({
  studentId,
  onClose,
}: {
  studentId: string;
  onClose: () => void;
}) {
  const repertoire = useAsync(() => getFamilyRepertoire(studentId), [studentId]);
  const current = repertoire.data?.current ?? [];
  const completed = repertoire.data?.completed.items ?? [];
  const completedTotal = repertoire.data?.completed.total ?? 0;

  return (
    <Dialog
      title="Repertoire"
      description="The music being learned, prepared and completed."
      onClose={onClose}
    >
      {repertoire.loading && !repertoire.data && <p className="empty">Loading repertoire…</p>}
      {repertoire.error && <div className="error small">{repertoire.error}</div>}
      {repertoire.data && current.length === 0 && completedTotal === 0 && (
        <p className="empty">No pieces have been added yet.</p>
      )}
      {current.length > 0 && <RepertoireDialogList heading="Working on" entries={current} />}
      {completed.length > 0 && (
        <RepertoireDialogList
          heading={
            completedTotal > completed.length
              ? `Completed (latest ${completed.length} of ${completedTotal})`
              : "Completed"
          }
          entries={completed}
        />
      )}
      <div className="student-home-section-link">
        <FamilyHomeLink to="/progress">Open Progress</FamilyHomeLink>
      </div>
    </Dialog>
  );
}

function RepertoireDialogList({
  heading,
  entries,
}: {
  heading: string;
  entries: readonly RepertoireEntry[];
}) {
  return (
    <section className="student-home-repertoire-group">
      <h3>{heading}</h3>
      <ul className="student-home-repertoire-list">
        {entries.map((entry) => (
          <li key={entry.id}>
            <strong>{pieceName(entry)}</strong>
            <div className="small muted">
              {REPERTOIRE_STATUS_LABELS[entry.status]}
              {entry.workOrCollection && ` · ${entry.workOrCollection}`}
              {entry.instrument && ` · ${entry.instrument}`}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One uniform, collapsible note card. The summary row is the same height on
 * every card whatever the note says; the full text only appears once opened.
 */
export function HomeNoteCard({
  date,
  title,
  meta,
  children,
  tracking,
  onClose,
}: {
  date: string;
  title: string;
  meta?: ReactNode;
  children: ReactNode;
  tracking?: { studentId: string; note: FamilyNoteIdentity };
  onClose?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details className="home-note-card" onToggle={(event) => {
      setOpen(event.currentTarget.open);
      if (!event.currentTarget.open) onClose?.();
    }}>
      <summary className="home-note-summary">
        <span className="home-note-date tabular">{date}</span>
        <span className="home-note-title">{title}</span>
        {meta && <span className="home-note-meta small muted">{meta}</span>}
      </summary>
      <div className="home-note-body">{tracking
        ? <NoteVisibility studentId={tracking.studentId} note={tracking.note} enabled={open}>{children}</NoteVisibility>
        : children}</div>
    </details>
  );
}

/** History previews stay bounded; the destination link opens the full collection. */
function homePreview<T>(items: readonly T[]): readonly T[] {
  return items.slice(0, HOME_NOTE_PREVIEW);
}

export function LessonNoteCards({
  lessons,
  loading = false,
  error = null,
  studentId,
  onRetry,
}: {
  lessons: MyStudentDetail["lessons"];
  loading?: boolean;
  error?: string | null;
  studentId?: string;
  onRetry?: () => void;
}) {
  const visible = homePreview(lessons);
  const [activeGroove, setActiveGroove] = useState<string | null>(null);

  return (
    <section className="student-home-notes" aria-labelledby="home-lesson-notes-heading">
      <div className="student-home-strip-head">
        <h2 id="home-lesson-notes-heading">Lesson notes</h2>
        <FamilyHomeLink
          className="small student-home-strip-link"
          to={`/notes${familyNotesPageSearch("", 1, studentId)}`}
        >
          All notes
        </FamilyHomeLink>
      </div>
      {error && (
        <div className="error" role="alert">
          Lesson notes could not be loaded. {onRetry && (
            <button type="button" className="quiet" onClick={onRetry}>Retry lesson notes</button>
          )}
        </div>
      )}
      {lessons.length === 0 ? error ? null : (
        <div className="panel">
          <p className="empty">{loading ? "Loading lesson notes…" : "No lesson notes yet."}</p>
        </div>
      ) : (
        <div className="home-note-list">
          {visible.map((lesson) => (
            <HomeNoteCard
              key={lesson.id}
              date={lesson.lessonDate ? formatDate(lesson.lessonDate) : "Lesson"}
              title={lesson.title}
              meta={lesson.tutorName}
              tracking={studentId ? { studentId, note: lesson } : undefined}
              onClose={() => setActiveGroove((current) =>
                lesson.grooves?.some((groove) => groove.token === current) ? null : current,
              )}
            >
              {lesson.content ? (
                <p className="notes"><LessonNoteText content={lesson.content} /></p>
              ) : (
                <p className="small muted">No written note for this lesson.</p>
              )}
              <LessonNoteFiles files={lesson.attachments ?? []} />
              <NeckDiagramSheets sheets={lesson.neckDiagrams ?? []} />
              <LessonNoteGrooves grooves={lesson.grooves ?? []} activeToken={activeGroove} onSelect={setActiveGroove} />
              {lesson.attendanceStatus && lesson.attendanceStatus !== "present" && (
                <span className="tag">{lesson.attendanceStatus}</span>
              )}
            </HomeNoteCard>
          ))}
        </div>
      )}
    </section>
  );
}

type HomePracticeSession = PracticeSession & {
  focusSegments?: {
    id: string;
    focusName: string;
    durationSeconds: number;
    notes: string | null;
    needsWork: boolean;
  }[];
};

export function practiceSessionTitle(session: PracticeSession): string {
  return session.name?.trim() ? session.name : "Practice session";
}

export function PracticeNoteCards({
  sessions,
  loading = false,
  error = null,
}: {
  sessions: readonly PracticeSession[];
  loading?: boolean;
  error?: string | null;
}) {
  const visible = homePreview(sessions);

  return (
    <section className="student-home-notes" aria-labelledby="home-practice-notes-heading">
      <div className="student-home-strip-head">
        <h2 id="home-practice-notes-heading">Practice notes</h2>
        <FamilyHomeLink className="small student-home-strip-link" to="/practice">
          All practice
        </FamilyHomeLink>
      </div>
      {error && sessions.length === 0 ? (
        <div className="panel">
          <p className="empty">Practice notes could not be loaded right now.</p>
        </div>
      ) : sessions.length === 0 ? (
        <p className="small muted student-home-strip-empty" aria-live={loading ? "polite" : undefined}>
          {loading ? "Loading practice notes…" : "No practice logged yet."}
        </p>
      ) : (
        <div className="home-note-list">
          {visible.map((session) => {
            const segments = (session as HomePracticeSession).focusSegments ?? [];
            return (
              <HomeNoteCard
                key={session.id}
                date={formatDate(session.startedAt)}
                title={practiceSessionTitle(session)}
                meta={formatDuration(session.durationSeconds)}
              >
                {session.notes && <p className="notes">{session.notes}</p>}
                {segments.length > 0 && (
                  <ul className="home-practice-segments">
                    {segments.map((segment) => (
                      <li key={segment.id}>
                        <span className="tabular">{formatDuration(segment.durationSeconds)}</span>{" "}
                        {segment.focusName}
                        {segment.needsWork && <span className="tag warn">struggled</span>}
                        {segment.notes && <p className="small muted">{segment.notes}</p>}
                      </li>
                    ))}
                  </ul>
                )}
                {!session.notes && segments.length === 0 && (
                  <p className="small muted">No notes were written for this session.</p>
                )}
                {session.needsWork && <span className="tag warn">struggled</span>}
                {session.autoStopped && (
                  <div className="small muted">The timer stopped this session automatically.</div>
                )}
              </HomeNoteCard>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Kept as the page-level name for callers that used the older family page. */
export function MyLessons(props: FamilyHomeProps) {
  return <FamilyHome {...props} />;
}

/**
 * Booking-disabled read-only lesson list.
 *
 * This older read model includes both approved and pending rows. Pending ones
 * are tagged so they never read as confirmed, and calendar export stays off
 * here; booking-enabled families get the richer Home dashboard instead.
 */
export function NextLessons({ upcomingBookings }: Pick<MyStudentDetail, "upcomingBookings">) {
  const [nextLesson, ...otherLessons] = upcomingBookings;
  return (
    <section aria-labelledby="family-next-lesson-heading">
      <h2 id="family-next-lesson-heading">Next lesson</h2>
      <div className="panel">
        {nextLesson ? (
          <NextLessonBookingRow lesson={nextLesson} />
        ) : (
          <p className="empty">Nothing booked at the moment.</p>
        )}
      </div>
      {otherLessons.length > 0 && (
        <details className="ui-disclosure student-home-more-lessons">
          <summary>Other upcoming lessons ({otherLessons.length})</summary>
          <div className="ui-disclosure-body panel">
            {otherLessons.map((lesson) => (
              <NextLessonBookingRow key={lesson.appointmentId} lesson={lesson} />
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function NextLessonBookingRow({
  lesson,
}: {
  lesson: MyStudentDetail["upcomingBookings"][number];
}) {
  return (
    <div className="slot">
      <time>{formatTime(lesson.startsAt)}</time>
      <div className="family-next-lesson-details">
        <div>{formatDate(lesson.startsAt)}</div>
        <div className="small muted">
          {lesson.serviceName ?? "Lesson"}
          {lesson.tutorName && ` · ${lesson.tutorName}`}
        </div>
        {lesson.awaitingConfirmation && (
          <span className="tag warn">Awaiting confirmation</span>
        )}
      </div>
    </div>
  );
}

/**
 * A groove task does not currently carry a lesson FK of its own, but the
 * assignment is created during the lesson write-up. Put it with the latest
 * published lesson at or before its creation time, bounded by the next lesson.
 * Metronome work has an explicit sourceLessonId and never uses this heuristic.
 */
export function familyLessonPracticeTools(
  lessons: MyStudentDetail["lessons"],
  todos: readonly PracticeTodo[],
  lessonIndex: number,
  newerLessonDate?: string | null,
): PracticeTodo[] {
  const lesson = lessons[lessonIndex];
  if (!lesson) return [];

  // Parity lesson ids are domain-qualified for the family UI (`school:...` /
  // `private:...`), while metronome assignments retain the canonical lesson id
  // that created them. Prefer the server's sourceId when present and keep the
  // legacy plain id as the fallback for private-only responses.
  const lessonSourceId =
    "sourceId" in lesson && typeof lesson.sourceId === "string" ? lesson.sourceId : lesson.id;
  const activeTodos = todos.filter((todo) => !todo.withdrawnAt);
  const exact = activeTodos.filter((todo) => todo.metronome?.sourceLessonId === lessonSourceId);
  if (!lesson.lessonDate) return exact;

  const from = Date.parse(lesson.lessonDate);
  const newer = lessons[lessonIndex - 1]?.lessonDate
    ?? (lessonIndex === 0 ? newerLessonDate : undefined);
  const to = newer ? Date.parse(newer) : Number.POSITIVE_INFINITY;
  const grooves = activeTodos.filter((todo) => {
    if (!todo.groove || todo.metronome?.sourceLessonId) return false;
    const created = Date.parse(todo.createdAt);
    return Number.isFinite(created) && created >= from && created < to;
  });
  const byId = new Map([...exact, ...grooves].map((todo) => [todo.id, todo]));
  return [...byId.values()];
}

/**
 * What the tutor attached to this note.
 *
 * Plain links rather than players: a family opening these is usually about to
 * print the sheet or put the recording on in the kitchen, and an audio element
 * per note on a page of thirty is a page that fetches thirty files to draw
 * itself.
 */
export function LessonNoteFiles({ files }: { files: readonly LessonNoteFile[] }) {
  if (files.length === 0) return null;

  return (
    <section className="family-lesson-files" aria-label="Files from this lesson">
      <h3>Files from this lesson</h3>
      <ul className="family-lesson-file-list">
        {files.map((file) => (
          <li key={file.id}>
            <a href={file.url} target="_blank" rel="noreferrer">
              {file.fileName}
            </a>
            {file.fileType && <span className="small muted"> · {file.fileType}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Notes are a separate destination, with interactive practice from each lesson. */
export function FamilyLessonNotes({ studentId }: { studentId: string }) {
  const [activeGroove, setActiveGroove] = useState<string | null>(null);
  const { me } = useAuth();
  const accountId = me?.user.id ?? "";
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStudentId = searchParams.get("student");
  const requestedLessonId = searchParams.get("lesson");
  const authorisedStudentIds = new Set([...(me?.studentIds ?? []), ...(me?.childStudentIds ?? [])]);
  const authorisedStudentRequest = Boolean(
    requestedStudentId && (requestedStudentId === studentId || authorisedStudentIds.has(requestedStudentId)),
  );
  const rejectedStudentRequest = Boolean(requestedStudentId && !authorisedStudentRequest);
  const waitingForStudent = Boolean(
    authorisedStudentRequest && requestedStudentId !== studentId,
  );
  const scopeStudentId = authorisedStudentRequest ? requestedStudentId! : studentId;
  const scope: FamilyNotesScope = {
    accountId,
    studentId: scopeStudentId,
    collection: FAMILY_NOTES_COLLECTION,
  };
  const historyScope = familyNotesScopeFromState(location.state);
  const scopeChanged = historyScope !== null && !isFamilyNotesScope(historyScope, scope);
  const requestedPage = scopeChanged || rejectedStudentRequest || requestedLessonId
    ? 1
    : familyNotesPage(searchParams.get("page"));
  const { data: response, error, loading, reload } = useAsync<MyStudentDetail | null>(
    () => (waitingForStudent
      ? Promise.resolve(null)
      : get<MyStudentDetail>(`/me/students/${encodeURIComponent(studentId)}`, {
          lessonNotesPage: String(requestedPage),
          lessonNotesPageSize: String(FAMILY_LESSON_NOTE_PAGE_SIZE),
          ...(requestedLessonId ? { lessonId: requestedLessonId } : {}),
        })),
    [accountId, studentId, requestedPage, requestedLessonId, waitingForStudent],
  );
  const practice = useAsync(() => getFamilyPracticeTodos(studentId), [accountId, studentId]);
  const data = response?.student.id === studentId ? response : null;
  const pageMetadata = data?.lessonNotesPage ?? null;
  const lessons = data?.lessons ?? [];
  const page = pageMetadata?.page ?? 1;
  const pages = pageMetadata?.pages ?? 1;

  useEffect(() => {
    if (waitingForStudent) {
      // The family shell consumes an authorised ?student= deep link. Keep its
      // safe selection/page parameters while removing every other query value.
      const safeSearch = familyNotesPageSearch(location.search, requestedPage, requestedStudentId ?? undefined);
      if (location.search !== safeSearch) {
        navigate({ pathname: location.pathname, search: safeSearch }, {
          replace: true,
          state: location.state,
        });
      }
      return;
    }

    const currentParams = new URLSearchParams(location.search);
    if (rejectedStudentRequest) { currentParams.delete("student"); currentParams.delete("lesson"); }
    const safeSearch = familyNotesPageSearch(
      currentParams.toString(),
      requestedPage,
    );
    const stateMatchesScope = isFamilyNotesScope(historyScope, scope);
    if (location.search !== safeSearch || !stateMatchesScope) {
      navigate({ pathname: location.pathname, search: safeSearch }, {
        replace: true,
        state: familyNotesRouteState(location.state, scope),
      });
    }
  }, [
    historyScope,
    location.pathname,
    location.search,
    location.state,
    navigate,
    rejectedStudentRequest,
    requestedPage,
    requestedStudentId,
    scope.accountId,
    scope.collection,
    scope.studentId,
    waitingForStudent,
  ]);

  useEffect(() => {
    if (!data || !pageMetadata || waitingForStudent || requestedPage <= pageMetadata.pages) return;
    const next = familyNotesPageSearch(location.search, pageMetadata.page);
    setSearchParams(new URLSearchParams(next.startsWith("?") ? next.slice(1) : next), {
      replace: true,
      state: familyNotesRouteState(location.state, scope),
    });
  }, [accountId, data, location.search, location.state, pageMetadata, requestedPage, setSearchParams, studentId, waitingForStudent]);

  if (waitingForStudent) return <p className="empty">Opening your learner’s notes…</p>;
  if (loading && !data) return <p className="empty">Loading…</p>;
  if (error && !data) {
    return (
      <div className="error" role="alert">
        {error} <button type="button" className="quiet" onClick={reload}>Retry notes</button>
      </div>
    );
  }
  if (!data) return null;

  // A Hub that predates note paging ignores the page parameters and returns
  // its whole recent list in one response. Show that list, without a pager,
  // rather than hiding every note until the Hub is updated.
  const meta = pageMetadata ?? {
    page: 1, pageSize: Math.max(1, lessons.length), total: lessons.length, pages: 1, hasMore: false, newerLessonDate: null,
  };

  const changePage = (nextPage: number) => {
    const targetPage = Math.min(pages, Math.max(1, nextPage));
    // The failed page is already in the address, so asking for it again is a retry.
    if (error && targetPage === requestedPage) { reload(); return; }
    const next = familyNotesPageSearch(location.search, targetPage);
    setSearchParams(new URLSearchParams(next.startsWith("?") ? next.slice(1) : next), {
      state: familyNotesRouteState(location.state, scope),
    });
  };
  const firstVisibleNote = meta.total === 0
    ? 0
    : (page - 1) * meta.pageSize + 1;
  const lastVisibleNote = Math.min(page * meta.pageSize, meta.total);

  return (
    <>
      <StudentHeading data={data} />
      <NoteRecipients base={`/me/students/${studentId}`} />

      <h2>Lesson notes</h2>
      {requestedLessonId && <p className="small"><Link to={"/notes?student=" + encodeURIComponent(studentId)}>All lesson notes</Link></p>}
      {loading && !requestedLessonId && page !== requestedPage && (
        <p className="small muted" role="status">Loading page {requestedPage}…</p>
      )}
      {error && (
        <div className="notice small" role="alert">
          Page {requestedPage} could not be loaded: {error} Your last loaded notes are still available.
          <button type="button" className="quiet" onClick={reload}>Retry page {requestedPage}</button>
        </div>
      )}
      {data && !error && !loading && !requestedLessonId && page !== requestedPage && (
        <p className="small muted" role="status">Showing page {page} of {pages}.</p>
      )}
      {!requestedLessonId && pageMetadata === null && lessons.length > 0 && (
        <p className="small muted">Showing your most recent lesson notes.</p>
      )}
      {!requestedLessonId && pageMetadata !== null && meta.total > 0 && (
        <p className="small muted" aria-live="polite">
          Showing lesson notes {firstVisibleNote}–{lastVisibleNote} of {meta.total}.
        </p>
      )}
      <a className="small" href="/help/read-your-lesson-notes" target="_blank" rel="noreferrer">Help with lesson notes</a>
      {practice.loading && lessons.length > 0 && (
        <p className="small muted family-lesson-practice-state" role="status">
          Loading practice links…
        </p>
      )}
      {practice.error && (
        <div className="notice small family-lesson-practice-state" role="status">
          Lesson notes are available, but practice links could not be loaded right now.
        </div>
      )}
      {requestedLessonId && lessons.length === 0 && !loading && !error && <p className="empty">This lesson note is no longer available.</p>}
      {!requestedLessonId && meta.total === 0 && (
        <div className="panel">
          <p className="empty">No notes yet.</p>
        </div>
      )}
      {lessons.map((lesson, pageIndex) => {
        const tools = familyLessonPracticeTools(
          lessons,
          practice.data ?? [],
          pageIndex,
          meta.newerLessonDate,
        );
        return (
          <article className="panel" id={"lesson-" + lesson.id} tabIndex={-1} key={lesson.id}>
            <NoteVisibility studentId={studentId} note={lesson}>
            <div className="spread">
              <strong>{lesson.title}</strong>
              <span className="small muted">
                {lesson.lessonDate ? formatDate(lesson.lessonDate) : ""}
                {lesson.tutorName && ` · ${lesson.tutorName}`}
              </span>
            </div>
            {lesson.content && <p className="notes"><LessonNoteText content={lesson.content} /></p>}
            {lesson.attendanceStatus && lesson.attendanceStatus !== "present" && (
              <span className="tag">{lesson.attendanceStatus}</span>
            )}
            <LessonNoteFiles files={lesson.attachments ?? []} />
              <NeckDiagramSheets sheets={lesson.neckDiagrams ?? []} />
            <LessonNoteGrooves grooves={lesson.grooves ?? []} activeToken={activeGroove} onSelect={setActiveGroove} />

            {tools.length > 0 && (
              <section
                className="family-lesson-practice-tools"
                aria-label="Practice from this lesson"
              >
                <h3>Practice from this lesson</h3>
                <div className="family-lesson-practice-stack">
                  {tools.map((todo) => (
                    <div className="notice family-lesson-practice-item" key={todo.id}>
                      <div className="spread">
                        <div className="family-lesson-practice-copy">
                          <strong>{todo.title}</strong>
                          {todo.description && (
                            <div className="small muted family-lesson-practice-description">
                              {todo.description}
                            </div>
                          )}
                          {todo.practiceSeconds > 0 && (
                            <div className="small muted family-lesson-practice-duration">
                              Practised {formatDuration(todo.practiceSeconds)}
                            </div>
                          )}
                        </div>
                        {todo.metronome ? (
                          <Link
                            className="button action-secondary"
                            to={`/practice/metronome?todo=${encodeURIComponent(todo.id)}`}
                          >
                            Open {todo.metronome.bpm} BPM metronome
                          </Link>
                        ) : todo.groove ? (
                          <Link className="button action-secondary" to={grooveSnapshotHref(todo.groove.token)}>
                            Open groove
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            </NoteVisibility>
          </article>
        );
      })}
      {!requestedLessonId && <Pagination
        page={page}
        pages={pages}
        onPageChange={changePage}
        ariaLabel="Family lesson notes pagination"
      />}
    </>
  );
}

export function StudentHeading({ data, tutors }: { data: MyStudentDetail; tutors?: readonly TutorPresentation[] }) {
  const { student } = data;

  return (
    <>
      <h1>{student.fullName}</h1>
      <p className="lede">
        {tutors ? homeTutorSummary(tutors) || student.instrument || "Lessons" : `${student.instrument ?? "Lessons"}${student.tutorName ? ` with ${student.tutorName}` : ""}`}
      </p>
    </>
  );
}
